import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpenIcon, QueueListIcon } from "@heroicons/react/24/outline";
import { focusRing } from "../../pages/Exams/TestBuilder/ui";
import usePassagesAvailable, { markPassagesAvailable } from "./usePassagesAvailable";

export { markPassagesAvailable, usePassagesAvailable };

// The bank's tabs and the same pair in My Questions (only the user's own questions and passages).
const BASES = { bank: "/questions", mine: "/my-questions" };
const TABS = [
  { key: "questions", path: "", label: "passages.tab_questions", Icon: QueueListIcon },
  { key: "texts", path: "/texts", label: "passages.tab_texts", Icon: BookOpenIcon },
];

// The tab just clicked; the bar of the page it opens takes the focus once.
let chosenTab = null;

/**
 * "Questions | Texts" switch of the question bank (`scope` "bank") or of My Questions ("mine"). `active` is
 * "questions" or "texts"; nothing is rendered without `showTexts` (no passages API), so the page looks as
 * before. `onLeave` runs before the other tab opens (e.g. to keep the page's filters for the way back);
 * `search` (e.g. "?folder=…") goes along, so the other tab opens on the same folder.
 */
export default function BankTabs({ active, showTexts, onLeave, scope = "bank", search = "", className = "" }) {
  const [t] = useTranslation();
  const activeRef = useRef(null);

  // A switch remounts the page under the tab bar: the focus goes to the chosen tab of the new bar.
  useEffect(() => {
    if (showTexts && chosenTab === active) {
      chosenTab = null;
      activeRef.current?.focus();
    }
  }, [showTexts, active]);

  if (!showTexts) return null;

  return (
    <nav aria-label={t("passages.tabs_label")} className={`flex w-fit max-w-full items-center gap-1 rounded-xl bg-slate-100 p-1 ${className}`}>
      {TABS.map(({ key, path, label, Icon }) => {
        const current = key === active;
        const to = BASES[scope] + path + search;
        return (
          <Link
            key={key}
            ref={current ? activeRef : undefined}
            to={to}
            replace={current}
            onClick={(event) => {
              if (current) return;
              onLeave?.();
              // Not for a tab opened in a new browser tab or window.
              if (!event.ctrlKey && !event.metaKey && !event.shiftKey) chosenTab = key;
            }}
            aria-current={current ? "page" : undefined}
            className={`inline-flex h-8 min-w-0 items-center gap-2 rounded-lg px-3 text-sm font-medium transition duration-200 ${focusRing} ${
              current ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
            }`}
          >
            <Icon className={`h-4 w-4 shrink-0 ${current ? "text-blue-600" : "text-slate-400"}`} aria-hidden="true" />
            <span className="truncate">{t(label)}</span>
          </Link>
        );
      })}
    </nav>
  );
}
